/**
 * MapDialogs - All dialog components for MapView
 * Handles: Create Run, Commit, Uncommit, Remove from Committed
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Lock, Unlock, CheckCircle, Archive } from 'lucide-react';

/**
 * Create Run Dialog
 */
export const CreateRunDialog = ({
  open,
  onOpenChange,
  runName,
  onRunNameChange,
  onSubmit,
  pending = false,
  dateLabel
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Create New Run</DialogTitle>
        <DialogDescription>
          Create a delivery run for {dateLabel}
        </DialogDescription>
      </DialogHeader>
      <div className="py-4">
        <Input
          placeholder="Run name (e.g., Morning Run, Driver A)"
          value={runName}
          onChange={(e) => onRunNameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (!pending) onSubmit();
          }}
          data-testid="new-run-name-input"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button disabled={pending} onClick={() => { if (!pending) onSubmit(); }} data-testid="create-run-submit">Create Run</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


/**
 * Commit Run Dialog
 */
export const CommitRunDialog = ({
  open,
  onOpenChange,
  onConfirm,
  pending = false,
  runName,
  stopCount
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-amber-600" />
          Commit Run
        </DialogTitle>
        <DialogDescription>
          Ready to dispatch "{runName}" with {stopCount} stops?
        </DialogDescription>
      </DialogHeader>
      <div className="py-4 space-y-3">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
          <strong>This will:</strong>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Change all order statuses to "On Route"</li>
            <li>Lock the run from further editing</li>
            <li>Make orders visible in the driver's app</li>
          </ul>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button 
          disabled={pending}
          onClick={() => { if (!pending) onConfirm(); }}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="commit-run-confirm"
        >
          <Lock className="h-4 w-4 mr-2" />
          Commit Run
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


/**
 * Uncommit Run Dialog
 */
export const UncommitRunDialog = ({
  open,
  onOpenChange,
  onConfirm,
  pending = false,
  runName,
  stopCount
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Unlock className="h-5 w-5 text-stone-600" />
          Uncommit Run
        </DialogTitle>
        <DialogDescription>
          Revert "{runName}" back to draft mode?
        </DialogDescription>
      </DialogHeader>
      <div className="py-4 space-y-3">
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-sm text-stone-700">
          <strong>This will:</strong>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Change unfinished order statuses back to "Designed"</li>
            <li>Unlock the run for editing</li>
            <li>Remove orders from the driver's active view</li>
            <li>Keep delivered orders credited to the driver who delivered them</li>
          </ul>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button 
          disabled={pending}
          onClick={() => { if (!pending) onConfirm(); }}
          variant="outline"
          data-testid="uncommit-run-confirm"
        >
          <Unlock className="h-4 w-4 mr-2" />
          Uncommit Run
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


/**
 * Remove from Committed Run Dialog
 */
export const RemoveFromCommittedDialog = ({
  open,
  onOpenChange,
  onConfirm,
  pending = false,
  orderNumber,
  recipientName,
  runName,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-red-600" />
          Remove from Committed Run
        </DialogTitle>
        <DialogDescription>
          Remove order #{orderNumber}{recipientName ? ` for ${recipientName}` : ''} from "{runName}"?
        </DialogDescription>
      </DialogHeader>
      <div className="py-4 space-y-3">
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
          <strong>This will:</strong>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Revert this order's status back to "Designed"</li>
            <li>Remove it from the driver's delivery list</li>
            <li>The order can be added to another run</li>
          </ul>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button 
          disabled={pending}
          onClick={() => { if (!pending) onConfirm(); }}
          variant="destructive"
          data-testid="remove-from-committed-confirm"
        >
          Remove Order
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);


/**
 * Close Run Dialog
 */
export const CloseRunDialog = ({
  open,
  onOpenChange,
  onConfirm,
  pending = false,
  runName,
  stopCount
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Archive className="h-5 w-5 text-stone-600" />
          Close Run
        </DialogTitle>
        <DialogDescription>
          Close "{runName}" with {stopCount} delivered stop{stopCount !== 1 ? 's' : ''}?
        </DialogDescription>
      </DialogHeader>
      <div className="py-4 space-y-3">
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-sm text-stone-700">
          <strong>This will:</strong>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Mark this run as completed</li>
            <li>Freeze all data — no further edits by driver or dispatcher</li>
          </ul>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button 
          disabled={pending}
          onClick={() => { if (!pending) onConfirm(); }}
          className="bg-stone-700 hover:bg-stone-800"
          data-testid="close-run-confirm"
        >
          <Archive className="h-4 w-4 mr-2" />
          Close Run
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
